import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { IncomeExportService } from '../services/income-export.service';
import {
  FeeResponsibility,
  IncomeItem,
  IncomePaymentMethod,
  IncomePaymentStage,
  IncomeService,
} from '../services/income.service';

const currentMonth = (() => {
  const today = new Date();
  return {
    from: new Date(today.getFullYear(), today.getMonth(), 1),
    to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
  };
})();

function toDateFilter(date: Date | null): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

@Component({
  selector: 'app-income-page',
  imports: [
    DatePipe,
    DecimalPipe,
    FormsModule,
    LucideAngularModule,
    DatePickerModule,
    InputTextModule,
    SelectModule,
    TableModule,
  ],
  templateUrl: './income-page.component.html',
})
export class IncomePageComponent implements OnInit {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly incomeService = inject(IncomeService);
  private readonly incomeExportService = inject(IncomeExportService);

  readonly items = signal<IncomeItem[]>([]);
  readonly searchTerm = signal('');
  readonly dateFrom = signal(toDateFilter(currentMonth.from));
  readonly dateTo = signal(toDateFilter(currentMonth.to));
  readonly paymentStage = signal<'ALL' | IncomePaymentStage>('ALL');
  readonly paymentMethod = signal<'ALL' | IncomePaymentMethod>('ALL');

  readonly pendingDateFrom = signal<Date | null>(new Date(currentMonth.from));
  readonly pendingDateTo = signal<Date | null>(new Date(currentMonth.to));
  readonly pendingPaymentStage = signal<'ALL' | IncomePaymentStage>('ALL');
  readonly pendingPaymentMethod = signal<'ALL' | IncomePaymentMethod>('ALL');
  readonly selectedIncomeId = signal<string | null>(null);
  readonly editReceiptUrl = signal('');
  readonly editQuotationUrl = signal('');
  readonly receiptUrlError = signal('');
  readonly quotationUrlError = signal('');

  readonly selectedIncome = computed(
    () => this.items().find((item) => item.id === this.selectedIncomeId()) ?? null,
  );

  readonly paymentStageOptions: Array<{
    label: string;
    value: 'ALL' | IncomePaymentStage;
  }> = [
    { label: 'ทุกงวดการชำระ', value: 'ALL' },
    { label: 'ค่าจอง', value: 'BOOKING' },
    { label: 'เงินมัดจำ', value: 'DEPOSIT' },
    { label: 'ยอดคงเหลือ', value: 'BALANCE' },
    { label: 'ชำระเต็มจำนวน', value: 'FULL_PAYMENT' },
  ];

  readonly paymentMethodOptions: Array<{
    label: string;
    value: 'ALL' | IncomePaymentMethod;
  }> = [
    { label: 'ทุกช่องทาง', value: 'ALL' },
    { label: 'โอนผ่านธนาคาร', value: 'BANK_TRANSFER' },
    { label: 'เงินสด', value: 'CASH' },
    { label: 'บัตรเครดิต', value: 'CREDIT_CARD' },
  ];

  readonly filteredItems = computed(() => {
    const query = this.searchTerm().trim().toLocaleLowerCase('th');
    if (!query) return this.items();
    return this.items().filter((item) =>
      [
        item.paymentNo,
        item.bookingNo,
        item.tourCode,
        item.tourName,
        item.customerName,
        item.payerName,
      ]
        .join(' ')
        .toLocaleLowerCase('th')
        .includes(query),
    );
  });

  readonly incomeTotal = computed(() =>
    this.filteredItems().reduce((total, item) => total + item.customerPaidAmount, 0),
  );

  readonly feeTotal = computed(() =>
    this.filteredItems().reduce((total, item) => total + item.creditCardFeeAmount, 0),
  );

  readonly companyFeeTotal = computed(() =>
    this.filteredItems()
      .filter((item) => item.creditCardFeeResponsibility === 'COMPANY')
      .reduce((total, item) => total + item.creditCardFeeAmount, 0),
  );

  ngOnInit(): void {
    void this.loadItems();
  }

  async applyFilters(): Promise<void> {
    this.dateFrom.set(toDateFilter(this.pendingDateFrom()));
    this.dateTo.set(toDateFilter(this.pendingDateTo()));
    this.paymentStage.set(this.pendingPaymentStage());
    this.paymentMethod.set(this.pendingPaymentMethod());
    await this.loadItems();
  }

  async resetFilters(): Promise<void> {
    this.searchTerm.set('');
    this.pendingDateFrom.set(new Date(currentMonth.from));
    this.pendingDateTo.set(new Date(currentMonth.to));
    this.pendingPaymentStage.set('ALL');
    this.pendingPaymentMethod.set('ALL');
    await this.applyFilters();
  }

  paymentStageLabel(stage: IncomePaymentStage): string {
    return this.paymentStageOptions.find((option) => option.value === stage)?.label ?? stage;
  }

  paymentMethodLabel(item: IncomeItem): string {
    const label =
      this.paymentMethodOptions.find((option) => option.value === item.paymentMethod)?.label ??
      item.paymentMethod;
    return item.paymentMethod === 'CREDIT_CARD' ? `${label} ${item.creditCardFeePercent}%` : label;
  }

  feeResponsibilityLabel(responsibility: FeeResponsibility): string {
    if (responsibility === 'CUSTOMER') return 'ลูกค้าชำระ';
    if (responsibility === 'COMPANY') return 'บริษัทรับภาระ';
    return 'ไม่มีค่าธรรมเนียม';
  }

  async exportExcel(): Promise<void> {
    const items = this.filteredItems();
    if (items.length === 0) {
      this.overlay.error('ไม่สามารถ Export ได้', 'ไม่พบรายการรายรับตามตัวกรองปัจจุบัน');
      return;
    }

    this.overlay.showLoading('กำลังสร้างไฟล์ Excel...');
    try {
      await this.incomeExportService.export(items, {
        dateFrom: this.dateFrom(),
        dateTo: this.dateTo(),
        paymentStage: this.optionLabel(this.paymentStageOptions, this.paymentStage()),
        paymentMethod: this.optionLabel(this.paymentMethodOptions, this.paymentMethod()),
        searchTerm: this.searchTerm().trim(),
      });
    } catch {
      this.overlay.error('สร้างไฟล์ Excel ไม่สำเร็จ', 'กรุณาลองใหม่อีกครั้ง');
    } finally {
      this.overlay.hideLoading();
    }
  }

  openEditLinks(item: IncomeItem): void {
    this.selectedIncomeId.set(item.id);
    this.editReceiptUrl.set(item.receiptUrl);
    this.editQuotationUrl.set(item.quotationUrl);
    this.clearLinkErrors();
  }

  closeEditLinks(): void {
    this.selectedIncomeId.set(null);
    this.editReceiptUrl.set('');
    this.editQuotationUrl.set('');
    this.clearLinkErrors();
  }

  async saveLinks(): Promise<void> {
    const receiptError = this.validateOptionalUrl(this.editReceiptUrl(), 'ลิงก์ใบเสร็จ');
    const quotationError = this.validateOptionalUrl(this.editQuotationUrl(), 'ลิงก์ใบเสนอราคา');
    this.receiptUrlError.set(receiptError);
    this.quotationUrlError.set(quotationError);
    if (receiptError || quotationError) return;

    const selectedId = this.selectedIncomeId();
    const selectedIncome = this.selectedIncome();
    if (!selectedId || !selectedIncome) return;
    const receiptUrl = this.editReceiptUrl().trim();
    const quotationUrl = this.editQuotationUrl().trim();

    const confirmed = await this.overlay.confirm({
      title: 'ยืนยันการแก้ไขเอกสาร',
      message: `ยืนยันแก้ไขลิงก์ใบเสร็จและใบเสนอราคาของ ${selectedIncome.paymentNo} หรือไม่`,
      confirmText: 'ยืนยันการแก้ไข',
      cancelText: 'ยกเลิก',
    });
    if (!confirmed) return;

    this.overlay.showLoading('กำลังบันทึกลิงก์เอกสาร...');
    try {
      await this.incomeService.updateDocuments(selectedId, { receiptUrl, quotationUrl });
      this.items.update((items) =>
        items.map((item) =>
          item.id === selectedId ? { ...item, receiptUrl, quotationUrl } : item,
        ),
      );
      this.closeEditLinks();
      this.overlay.success('บันทึกสำเร็จ', 'แก้ไขลิงก์ใบเสร็จและใบเสนอราคาเรียบร้อยแล้ว');
    } catch (error: unknown) {
      this.overlay.error('บันทึกลิงก์เอกสารไม่สำเร็จ', this.errorMessage(error));
    } finally {
      this.overlay.hideLoading();
    }
  }

  openDocument(url: string): void {
    if (!url) return;
    window.open(this.incomeService.resolveFileUrl(url), '_blank', 'noopener,noreferrer');
  }

  clearReceiptUrlError(): void {
    this.receiptUrlError.set('');
  }

  clearQuotationUrlError(): void {
    this.quotationUrlError.set('');
  }

  private clearLinkErrors(): void {
    this.receiptUrlError.set('');
    this.quotationUrlError.set('');
  }

  private validateOptionalUrl(value: string, label: string): string {
    const url = value.trim();
    if (!url) return '';
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
        ? ''
        : `${label}ต้องขึ้นต้นด้วย http:// หรือ https://`;
    } catch {
      return `${label}ไม่ถูกต้อง`;
    }
  }

  private async loadItems(): Promise<void> {
    this.overlay.showLoading('กำลังโหลดรายการรายรับ...');
    try {
      this.items.set(
        await this.incomeService.getItems({
          dateFrom: this.dateFrom(),
          dateTo: this.dateTo(),
          paymentStage: this.paymentStage(),
          paymentMethod: this.paymentMethod(),
        }),
      );
    } catch (error: unknown) {
      this.overlay.error('โหลดรายการรายรับไม่สำเร็จ', this.errorMessage(error));
    } finally {
      this.overlay.hideLoading();
    }
  }

  private errorMessage(error: unknown): string {
    return error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
      ? error.error.message
      : 'กรุณาลองใหม่อีกครั้ง';
  }

  private optionLabel<T extends string>(
    options: Array<{ label: string; value: T }>,
    value: T,
  ): string {
    return options.find((option) => option.value === value)?.label ?? value;
  }
}
